/* eslint-disable no-mixed-spaces-and-tabs */
import { useGridApiRef } from "@mui/x-data-grid-pro-6";
import { useConfirm } from "material-ui-confirm";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import { updateWorkerSkillLevel } from "../../../../export/workers";
import ColumnGroupJobHeader from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-group-job-header";
import ColumnPriorityHeader from "../../../../ts/components/cross-skilling/ui/cross-skill-table/ui/column-priority-header";
import { CROSS_SKILLING_PERMISSIONS } from "../../../../ts/config/permissions";
import { hasPermissionString } from "../../../../ts/utils/helpers";
import useTableHeight from "../../../../ts/utils/use-table-height";
import {
	addSkillInterested,
	//rejectSkillInterested,
} from "../../../features/ManageWorkers/ManageWorkersSlice";
import {
	filterWorkersBySkillLevel,
	formatSkillsByJobs,
	getLowestLevelObject,
} from "../../../utils/helpers";
import {
	createJobGroupObject,
	createNonPrioritizedOtherSkillsGroup,
	createPrioritizedOtherSkillsGroup,
	createPriorityObject,
} from "../ui/cross-skill-table/helper";
import {
	filterWorkers,
	skillsWithoutJobs,
} from "../ui/cross-skill-table/utils";
import useCrossSkillData from "./use-cross-skill-data";

const useCrossSkillTableController = ({
	data,
	filters,
	allSkillLevels,
	allJobs,
	getFilter,
	searchText,
	appliedFilters = [],
}) => {
	const { t } = useTranslation();
	const confirm = useConfirm();
	const dispatch = useDispatch();
	let tempData = [...data];

	const {
		addSkillInterestStatus,
		rejectSkillInterestStatus,
		updateWorkerSkillLevelError,
		updateWorkerSkillLevelStatus,
		allSkills,
    allManagers,
		fetchManageWorkersDataStatus,
		skillMatrixFiltersStatus,
		skillMatrixDefaultFiltersStatus,
		skillPriorities,
		isSkillPriorityEnabled,
		permissions,
		isJobSkillLevelMapping,
		skillMatrixFiltersFilterRelationships,
	} = useCrossSkillData();

	let tempWorkers = [];
	const { height, boxRef } = useTableHeight();
	let columnGroup = [];
	const dataGridRef = useRef();
	const apiRef = useGridApiRef();
	const allSkillsOfAllLocationsFull = allSkills.map((skill) => {
		return { ...skill, skillId: skill.id };
	});

	// Compute allowed job IDs and skill IDs when location or work center filter is active
	const { allowedSkillIds, allowedJobIds, selectedLocationIds } = (() => {
		const locationAppliedFilter = appliedFilters.find((f) => f.key === "locations");
		const selectedLocationIds = locationAppliedFilter?.data?.map((l) => l.id) || [];
		const wcAppliedFilter = appliedFilters.find((f) => f.key === "workCenters");
		const selectedWCIds = wcAppliedFilter?.data?.map((wc) => wc.id) || [];

		if (selectedLocationIds.length === 0 && selectedWCIds.length === 0) {
			return { allowedSkillIds: null, allowedJobIds: null, selectedLocationIds: [] };
		}
		if (!skillMatrixFiltersFilterRelationships?.length) {
			return { allowedSkillIds: null, allowedJobIds: null, selectedLocationIds };
		}

		const locToWCRel = skillMatrixFiltersFilterRelationships.find(
			(rel) => rel.from === "locations" && rel.to === "workCenters"
		);
		const wcToJobRel = skillMatrixFiltersFilterRelationships.find(
			(rel) => rel.from === "workCenters" && rel.to === "jobs"
		);
		const jobToSkillRel = skillMatrixFiltersFilterRelationships.find(
			(rel) => rel.from === "jobs" && rel.to === "skills"
		);

		if (!wcToJobRel?.mappings || !jobToSkillRel?.mappings) {
			return { allowedSkillIds: null, allowedJobIds: null };
		}

		let effectiveWCIds = [...selectedWCIds];

		// If locations are selected, find corresponding work centers
		if (selectedLocationIds.length > 0 && locToWCRel?.mappings) {
			const wcFromLocs = new Set();
			for (const locId of selectedLocationIds) {
				const mapping = locToWCRel.mappings.find((m) => m.fromId === locId);
				if (mapping?.toIds) {
					for (const wcId of mapping.toIds) wcFromLocs.add(wcId);
				}
			}

			if (selectedWCIds.length > 0) {
				// If both are selected, we intersect
				effectiveWCIds = effectiveWCIds.filter(id => wcFromLocs.has(id));
			} else {
				// Only locations are selected
				effectiveWCIds = Array.from(wcFromLocs);
			}
		}

		if (effectiveWCIds.length === 0 && (selectedLocationIds.length > 0 || selectedWCIds.length > 0)) {
			// If filters are applied but result in no work centers, we should show nothing
			return { allowedSkillIds: new Set(), allowedJobIds: new Set() };
		}

		const allowedJobIds = new Set();
		for (const wcId of effectiveWCIds) {
			const mapping = wcToJobRel.mappings.find((m) => m.fromId === wcId);
			if (mapping?.toIds) {
				for (const jobId of mapping.toIds) allowedJobIds.add(jobId);
			}
		}

		const allowedSkillIds = new Set();
		for (const jobId of allowedJobIds) {
			const mapping = jobToSkillRel.mappings.find((m) => m.fromId === jobId);
			if (mapping?.toIds) {
				for (const skillId of mapping.toIds) allowedSkillIds.add(skillId);
			}
		}

		return { allowedSkillIds, allowedJobIds, selectedLocationIds };
	})();

	const allSkillsOfAllLocations = allowedSkillIds
		? allSkillsOfAllLocationsFull.filter((skill) => allowedSkillIds.has(skill.id))
		: allSkillsOfAllLocationsFull;

	const isJobFilterOld = getFilter("job").jobs.length > 0;
	const isSkillFilterOld = getFilter("skill").skills.length > 0;
	const isJobFilterNew = appliedFilters.some((f) => f.key === "jobs" && f.data?.length > 0);
	const isSkillFilterNew = appliedFilters.some((f) => f.key === "skills" && f.data?.length > 0);
	const isJobFilter = isJobFilterOld || isJobFilterNew;
	const isSkillFilter = isSkillFilterOld || isSkillFilterNew;
	const isDefaultFilter = !isJobFilter && !isSkillFilter;

	const [isColumnGroup, setIsColumnGroup] = useState(true);

	const isLocationTypeFilter = getFilter("location").locations.length > 0;
	const isWorkerTypeFilter = getFilter("workerType").workerTypes.length > 0;
	const isWorkerSubTypeFilter =
		getFilter("workerSubType").workerSubTypes.length > 0;

	const isSkillPriorityFilterOld =
		getFilter("skillPriority").skillPrioritys.length > 0;
	const isSkillPriorityFilterNew = appliedFilters.some((f) => f.key === "skillPriorities" && f.data?.length > 0);
	const isSkillPriorityFilter = isSkillPriorityFilterOld || isSkillPriorityFilterNew;
	const isWorkCenterFilter = getFilter("workCenter").workCenters.length > 0;
	const isShiftPatternFilter =
		getFilter("shiftPattern").shiftPatterns.length > 0;
	const isManagerFilter = getFilter("manager").managers.length > 0;

	const workerTypeIds = isWorkerTypeFilter
		? getFilter("workerType").workerTypes.map((workerType) => workerType.id)
		: [];
	const workerSubTypeIds = isWorkerSubTypeFilter
		? getFilter("workerSubType").workerSubTypes.map(
				(workerSubType) => workerSubType.id
		  )
		: [];
	const skillPriorityIdsOld = isSkillPriorityFilterOld
		? getFilter("skillPriority").skillPrioritys.map(
				(skillPriority) => skillPriority.id
		  )
		: [];
	const skillPriorityIdsNew = isSkillPriorityFilterNew
		? appliedFilters.find((f) => f.key === "skillPriorities").data.map((p) => p.id)
		: [];
	const skillPriorityIds = [...new Set([...skillPriorityIdsOld, ...skillPriorityIdsNew])];
  // Also include managerIds if in allManagers managerId matches with manager id
  const filterManagerIds = isManagerFilter
    ? getFilter("manager").managers.map((manager) => manager.id)
    : [];

  const getAllReportees = (managerIds, allManagers) => {
    let reportees = new Set(managerIds);
    let queue = [...managerIds];

    while (queue.length > 0) {
      const currentManagerId = queue.shift();
      const directReportees = allManagers
        .filter((m) => m.managerId === currentManagerId)
        .map((m) => m.id);

      for (const reporteeId of directReportees) {
        if (!reportees.has(reporteeId)) {
          reportees.add(reporteeId);
          queue.push(reporteeId);
        }
      }
    }
    return Array.from(reportees);
  };

  const managerIds = isManagerFilter
    ? getAllReportees(filterManagerIds, allManagers)
    : [];
	const workCenterIds = isWorkCenterFilter
		? getFilter("workCenter").workCenters.map((workCenter) => workCenter.id)
		: [];
	const shiftPatternIds = isShiftPatternFilter
		? getFilter("shiftPattern").shiftPatterns.map(
				(shiftPattern) => shiftPattern.id
		  )
		: [];

	// Replace all the individual filter blocks with this
	const typeFilters = [
		{
			isEnabled: isWorkerTypeFilter,
			field: "typeId",
			ids: workerTypeIds,
			parseIds: true,
		},
		{
			isEnabled: isWorkerSubTypeFilter,
			field: "workerSubTypeId",
			ids: workerSubTypeIds,
			parseIds: true,
		},
		{
			isEnabled: isManagerFilter,
			field: "managerId",
			ids: managerIds,
			parseIds: true,
			checkNonEmpty: true,
		},
		{
			isEnabled: isLocationTypeFilter,
			field: "locationId",
			ids: getFilter("location").locations.map((l) => l.id),
			checkNonEmpty: true,
		},
		{
			isEnabled: isWorkCenterFilter,
			field: "workcenterId",
			ids: workCenterIds,
			checkNonEmpty: true,
		},
		{
			isEnabled: isShiftPatternFilter,
			field: "shiftPatternId",
			ids: shiftPatternIds,
			checkNonEmpty: true,
		},
	];

	tempData = typeFilters.reduce(
		(filteredData, filterConfig) => {
			return filterWorkers(filteredData, filterConfig);
		},
		[...data]
	);

	tempWorkers = tempData;

	// Filter workers by skill priority - remove workers who don't have any skill with the selected priority
	if (isSkillPriorityFilter && skillPriorityIds.length > 0) {
		const skillIdsWithMatchingPriority = new Set(
			allSkillsOfAllLocations
				.filter((sk) => sk.priority && skillPriorityIds.includes(sk.priority.id))
				.map((sk) => sk.skillId)
		);
		tempWorkers = tempWorkers.filter((worker) =>
			worker.skills && worker.skills.some((ws) => skillIdsWithMatchingPriority.has(ws.skillId))
		);
		tempData = tempWorkers;
	}

	// Filter workers by job - remove workers who don't have any of the selected jobs
	if (isJobFilter) {
		const oldJobIdsList = getFilter("job").jobs.map((job) => job.id);
		const newJobIdsList = (appliedFilters.find((f) => f.key === "jobs")?.data || []).map((j) => j.id);
		const selectedJobIds = new Set([...oldJobIdsList, ...newJobIdsList]);
		if (selectedJobIds.size > 0) {
			tempWorkers = tempWorkers.filter((worker) => {
				const workerJobIds = worker.jobIds?.split(",").map((id) => parseInt(id)) ?? [];
				return workerJobIds.some((id) => selectedJobIds.has(id));
			});
			tempData = tempWorkers;
		}
	}

	const oldSkillIds = getFilter("skill").skills.map((skill) => skill.id);
	const newSkillIds = (appliedFilters.find((f) => f.key === "skills")?.data || []).map((s) => s.id);
	const skillIds = isSkillFilter
		? [...new Set([...oldSkillIds, ...newSkillIds])]
		: [];
	const skillLevelIds = isSkillFilter
		? getFilter("skill").skills.filter((skill) => skill.levelIds?.length > 0)
		: [];

	const filteredSkills = allSkillsOfAllLocations.filter((skill) =>
		skillIds.includes(skill.skillId)
	);

	const jobIdsSkillsArr =
		isJobFilter || isDefaultFilter
			? []
			: isSkillFilter
			? filteredSkills
			: allSkillsOfAllLocations;

	const oldJobIds = getFilter("job").jobs.map((job) => job.id);
	const newJobIds = (appliedFilters.find((f) => f.key === "jobs")?.data || []).map((j) => j.id);
	const allJobIds = (allJobs ?? []).map((job) => job.id);
	const jobIds = isDefaultFilter
		? (allowedJobIds ? allJobIds.filter((id) => allowedJobIds.has(id)) : allJobIds)
		: isJobFilter
		? [...new Set([...oldJobIds, ...newJobIds])]
		: [];

	(isJobFilter || isDefaultFilter) &&
		formatSkillsByJobs(
			allJobs ?? [],
			jobIdsSkillsArr,
			jobIds,
			isSkillFilter ? filteredSkills : (allowedSkillIds ? allSkillsOfAllLocations : []),
			skillPriorityIds
		);

	const filteredWorkersByLevel = isSkillFilter
		? filterWorkersBySkillLevel(tempWorkers, filters ?? [], skillIds)
		: [];
	const workers = tempWorkers;
	const handleAddSkillInterested = useCallback(
		(params, skill, setState) => {
			if (
				hasPermissionString(permissions, CROSS_SKILLING_PERMISSIONS.ACCEPT_INTEREST)
			) {
				const interestedSkill = params.row.skillInterests
					? params.row.skillInterests.find(
							(x) => parseInt(x.skillId) === parseInt(skill.skillId)
					  )
					: false;
				const isUpgrade = interestedSkill?.skillLevelId && parseInt(interestedSkill.skillLevelId) > 0;
				const targetLevel = isUpgrade
					? allSkillLevels.find((l) => l.id === parseInt(interestedSkill.skillLevelId))
					: getLowestLevelObject(allSkillLevels);
				const skillLevel = targetLevel || getLowestLevelObject(allSkillLevels);
				confirm({
					title: isUpgrade
						? t("cross-skilling_upgrade-skill-for-worker")
						: t("cross-skilling_update-skill-for-worker"),
					description: isUpgrade
						? t("cross-skilling_upgrading-worker-skill", {
								name: params.row.firstName,
								skill: skill.name,
								level: skillLevel.name,
						  })
						: t("cross-skilling_adding-skill-to-worker", {
								skill: skill.name,
								name: params.row.firstName,
						  }),
					confirmationText: isUpgrade
						? t("cross-skilling_upgrade-uppercase")
						: t("cross-skilling_update-uppercase"),
					cancellationText: t("cross-skilling_cancel-uppercase"),
				})
					.then(() => {
						dispatch(addSkillInterested({ id: interestedSkill.id }));
						setState((state) => ({
							...state,
							level: skillLevel.level,
							levelId: skillLevel.levelId ?? skillLevel.id,
							repCode: skillLevel.repCode,
							backColorCode: skillLevel.backColorCode,
							foreColorCode: skillLevel.foreColorCode,
							value: skillLevel.value,
							isInterest: false,
							skillAssigned: true,
						}));
					})
					.catch(() => {
						/* ... */
					});
			}
		},
		[dispatch, confirm, permissions, allSkillLevels, t]
	);

	const [skillsUpdatedListInSession, setOnUpdateSkillLevel] = useState([]);

	const onUpdateSkillLevel = useCallback(
		(state, obj, level) => {
			const updatedSkillLevels = skillsUpdatedListInSession.some(
				(x) => x.skillId === obj.skillId && x.workerId === obj.workerId
			)
				? skillsUpdatedListInSession.map((x) =>
						x.skillId === obj.skillId && x.workerId === obj.workerId
							? {
									...state,
									name: level.name,
									level: level.level,
									levelId: level.levelId,
									repCode: level.repCode,
									backColorCode: level.backColorCode,
									foreColorCode: level.foreColorCode,
									value: level.value,
									workerId: obj.workerId,
									skillId: obj.skillId,
							  }
							: x
				  )
				: [
						...skillsUpdatedListInSession,
						{
							...state,
							name: level.name,
							level: level.level,
							levelId: level.levelId,
							repCode: level.repCode,
							backColorCode: level.backColorCode,
							foreColorCode: level.foreColorCode,
							value: level.value,
							workerId: obj.workerId,
							skillId: obj.skillId,
						},
				  ];

			setOnUpdateSkillLevel(updatedSkillLevels);
		},
		[skillsUpdatedListInSession]
	);

	const handleSkillLevels = useCallback(
		(params, skill, level, setState, state) => {
			const obj = {
				skillId: skill.skillId,
				levelId: level.levelId,
				presentLevelId: state ? state.levelId : -1,
				workerId: params.id,
			};

			const workerSkill = params.row.skills.find(
				(x) => x.skillId === skill.skillId
			);

			if (workerSkill) {
				const currentLevelId = state ? state.levelId : workerSkill.levelId;
				if (currentLevelId !== level.levelId) {
					confirm({
						title: t("cross-skilling_update-skill-level-for-worker"),
						description: t("cross-skilling_skill-level-will-be-updated", {
							name: params.row.firstName,
							skill: workerSkill.skillName,
							level: level.name,
						}),
						confirmationText: t("cross-skilling_update-uppercase"),
						cancellationText: t("cross-skilling_cancel-uppercase"),
					})
						.then(() => {
							dispatch(updateWorkerSkillLevel(obj));
							setState((state) => ({
								...state,
								name: level.name,
								level: level.level,
								levelId: level.levelId,
								repCode: level.repCode,
								backColorCode: level.backColorCode,
								foreColorCode: level.foreColorCode,
								value: level.value,
							}));
							onUpdateSkillLevel(state, obj, level);
						})
						.catch(() => {
							/* ... */
						});
				}
			} else {
				const canConfirm = state ? state.levelId !== level.levelId : true;
				if (canConfirm) {
					if (!state) {
						confirm({
							title: t("cross-skilling_update-skill-level-for-worker"),
							description: t("cross-skilling_skill-level-will-be-updated", {
								name: params.row.firstName,
								skill: skill.name,
								level: level.name,
							}),
							confirmationText: t("cross-skilling_update-uppercase"),
							cancellationText: t("cross-skilling_cancel-uppercase"),
						})
							.then(() => {
								dispatch(updateWorkerSkillLevel(obj));
								setState((state) => ({
									...state,
									name: level.name,
									level: level.level,
									levelId: level.levelId,
									repCode: level.repCode,
									backColorCode: level.backColorCode,
									foreColorCode: level.foreColorCode,
									value: level.value,
								}));
								onUpdateSkillLevel(state, obj, level);
							})
							.catch(() => {
								/* ... */
							});
					} else {
						confirm({
							title: t("cross-skilling_update-skill-level-for-worker"),
							description: t("cross-skilling_skill-level-will-be-updated", {
								name: params.row.firstName,
								skill: skill.name,
								level: level.name,
							}),
							confirmationText: t("cross-skilling_update-uppercase"),
							cancellationText: t("cross-skilling_cancel-uppercase"),
						})
							.then(() => {
								dispatch(updateWorkerSkillLevel(obj));
								setState((state) => ({
									...state,
									name: level.name,
									level: level.level,
									levelId: level.levelId,
									repCode: level.repCode,
									backColorCode: level.backColorCode,
									foreColorCode: level.foreColorCode,
									value: level.value,
								}));
								onUpdateSkillLevel(state, obj, level);
							})
							.catch(() => {
								/* ... */
							});
					}
				}
			}
		},
		[dispatch, confirm, onUpdateSkillLevel, t]
	);

	const createNonPriorityJobGroup = (jobId, jobIdsSkillsArr) => {
		const temp = [];
		const obj = {};

		jobIdsSkillsArr.forEach((sk) => {
			if (jobId === sk.jobId) {
				temp.push({ field: `${sk.skillId}-${sk.jobId}` });
				obj.className = "job-header";
				obj.headerClassName = "job-header";
				obj.groupId = `${jobId}`;
				obj.headerName = sk.jobName;
				obj.children = temp;
				obj.renderHeaderGroup = () => (
					<ColumnGroupJobHeader skill={sk} searchText={searchText} />
				);
			}
		});

		return obj;
	};

	const createOtherSkillsGroup = (skillsWithNoJobs, skillPriorities, job) => {
		if (skillPriorities.length > 0 && isSkillPriorityEnabled) {
			const prioritiesWithSkills = getPrioritiesWithSkills(
				skillsWithNoJobs,
				skillPriorities
			);
			return createPrioritizedOtherSkillsGroup(
				prioritiesWithSkills,
				job,
				searchText
			);
		}

		return createNonPrioritizedOtherSkillsGroup(skillsWithNoJobs, searchText);
	};

	const getPrioritiesWithSkills = (skillsWithNoJobs, skillPriorities) => {
		return skillPriorities
			.map((priority) => ({
				...priority,
				skills: skillsWithNoJobs.filter(
					(skill) => skill.priority.id === priority.id
				),
			}))
			.filter((priority) => priority.skills.length > 0);
	};

	if ((isJobFilter || isDefaultFilter) && jobIds && jobIdsSkillsArr) {
		jobIds.forEach((jobId) => {
 		const job = (allJobs ?? []).find((j) => j.id === jobId);
			if (!job) return;

			const priorities = isSkillPriorityEnabled ? job.priorities : undefined;
			// Filter skills within priorities when location or work center filter is active
			const filteredPriorities = priorities && allowedSkillIds
				? priorities.map((p) => ({
					...p,
					skills: p.skills.filter((sk) => allowedSkillIds.has(sk.id)),
				})).filter((p) => p.skills.length > 0)
				: priorities;
			const groupObject = filteredPriorities
				? createJobGroupObject(job, filteredPriorities, searchText)
				: createNonPriorityJobGroup(jobId, jobIdsSkillsArr);

			if (Object.keys(groupObject).length > 0) {
				columnGroup.push(groupObject);
			}

			// Handle other skills group
			const shouldCreateOtherSkillsGroup =
				allSkillsOfAllLocations.length > 0 &&
				(allJobs ?? []).length > 0 &&
				!columnGroup.find((x) => x.groupId === "-1") &&
				!isJobFilter &&
				isColumnGroup &&
				!isWorkCenterFilter &&
				!selectedLocationIds?.length;

			if (shouldCreateOtherSkillsGroup) {
				const skillsWithNoJobs =
					(allJobs ?? []).length === 0
						? allSkillsOfAllLocations
						: skillsWithoutJobs(allSkillsOfAllLocations, allJobs ?? []);

				const otherSkillsGroup = createOtherSkillsGroup(
					skillsWithNoJobs,
					skillPriorities,
					job,
					searchText
				);
				if (Object.keys(otherSkillsGroup).length > 0) {
					columnGroup.push(otherSkillsGroup);
				}
			}
		});
	}
	// Handle jobs with no skills
	if (jobIds.length > 0 && !(isSkillPriorityFilter && skillPriorityIds.length > 0)) {
		const jobsWithNoSkills = jobIds.filter(
			(jobId) => (!columnGroup.some((col) => col.groupId === jobId.toString())
        || columnGroup.some((col) => col.groupId === jobId.toString() && col.children.length === 0)
      )
		);

		const noSkillColumns = jobsWithNoSkills
			.map((jobId) => {
 			const job = (allJobs ?? []).find((j) => j.id === jobId);
				if (!job) return null;

				return {
					groupId: jobId.toString(),
					headerName: job.name,
					children: [{ field: `${jobId}--2` }], // -2 represents no skill
					noSkill: true,
					jobColor: job.hexColor,
					renderHeaderGroup: () => (
						<ColumnGroupJobHeader
							skill={{
								skillId: -2,
								skillName: t("cross-skilling_no-skill"),
								jobName: job.name,
								jobColor: job.hexColor,
							}}
							searchText={searchText}
						/>
					),
				};
			})
			.filter(Boolean);

    // Remove existing column Group
    columnGroup = columnGroup.filter((x) => {
      const existing = noSkillColumns.find((y) => y.groupId === x.groupId);
      if (existing) {
        return false;
      }
      return true;
    });
		columnGroup.push(...noSkillColumns);
	}

  // Ensure columnGroup do not have duplicate entries based on groupId
  columnGroup = columnGroup.filter((x, i) => columnGroup.findIndex((y) => y.groupId === x.groupId) === i);

  // When skill priority filter is active, remove job columns that have no matching skills
  if (isSkillPriorityFilter && skillPriorityIds.length > 0) {
    const hasSkillChildren = (group) => {
      if (!group.children || group.children.length === 0) return false;
      return group.children.some((child) =>
        child.field ? true : hasSkillChildren(child)
      );
    };
    columnGroup = columnGroup.filter((group) => hasSkillChildren(group));
  }

	useEffect(() => {
		if (isJobFilter || isDefaultFilter) {
			setIsColumnGroup(true);
		} else {
			setIsColumnGroup(false);
		}
	}, [isJobFilter, isDefaultFilter]);

	return {
		dataGridRef,
		apiRef,
		columnGroup,
		height,
		boxRef,
		addSkillInterestStatus,
		rejectSkillInterestStatus,
		isJobFilter,
		isSkillPriorityFilter,
		skillPriorityIds,
		isSkillFilter,
		isDefaultFilter,
		isColumnGroup,
		skillLevelIds,
		jobIdsSkillsArr,
		jobIds,
		allSkillsOfAllLocations,
		filteredWorkersByLevel,
		filteredSkills,
		workers,
		handleAddSkillInterested,
		handleSkillLevels,
		updateWorkerSkillLevelError,
		updateWorkerSkillLevelStatus,
		fetchManageWorkersDataStatus,
		skillMatrixFiltersStatus,
		skillMatrixDefaultFiltersStatus,
		skillsUpdatedListInSession,
		onUpdateSkillLevel,
		isWorkCenterFilter,
		isJobSkillLevelMapping,
		selectedLocationIds,
	};
};

export default useCrossSkillTableController;
